/**
 * AgencySharePage.jsx
 * ------------------------------------------------------------------
 * Public read-only dashboard for an agency's end-client. Mounted at
 * `/agency-share/:token`. No login required — token in URL grants
 * read + approve access; agency revokes it from their panel.
 *
 * Layout:
 *   • Top bar with agency branding (logo, company, contact) — pulled
 *     from `agency_branding` doc via the share payload.
 *   • Client + domain card.
 *   • Latest scan summary (score, grades, top 5 issues).
 *   • GEO pages grid with preview iframe + approve/reject buttons.
 *   • Footer "Powered by TopRankPulse" — viral channel, can't be
 *     disabled here (agency only controls the agency-side PDF/widget
 *     toggle).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../config";
import {
  Loader2, AlertCircle, CheckCircle, XCircle, ExternalLink,
  Eye, X, MapPin, ShoppingCart, Folder, FileQuestion, Sparkles,
  AlertTriangle, ShieldCheck,
} from "lucide-react";

const GRADE_COLOURS = {
  A: "text-emerald-300 bg-emerald-900/40 border-emerald-700/40",
  B: "text-lime-300 bg-lime-900/40 border-lime-700/40",
  C: "text-amber-300 bg-amber-900/40 border-amber-700/40",
  D: "text-orange-300 bg-orange-900/40 border-orange-700/40",
  F: "text-rose-300 bg-rose-900/40 border-rose-700/40",
};
const TYPE_ICONS = { product: ShoppingCart, category: Folder, faq: FileQuestion };

export default function AgencySharePage() {
  const { token } = useParams();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewPage, setPreviewPage] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [approvalLoading, setApprovalLoading] = useState({});

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get(`${API}/share/${token}`).then(r => {
      if (!cancelled) setPayload(r.data);
    }).catch(e => {
      if (cancelled) return;
      setError(e?.response?.status === 404
        ? "Tento share link nie je platný alebo bol zrušený."
        : "Nepodarilo sa načítať dáta.");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const openPreview = async (page) => {
    setPreviewPage(page); setPreviewHtml("");
    try {
      const r = await axios.get(`${API}/share/${token}/page/${page.id}`);
      setPreviewHtml(r.data?.html || "<!-- empty -->");
    } catch { setPreviewHtml("<!-- failed -->"); }
  };

  const handleApproval = async (page, status) => {
    setApprovalLoading(prev => ({ ...prev, [page.id]: true }));
    try {
      const r = await axios.post(`${API}/share/${token}/page/${page.id}/approval`,
        { status });
      // Patch the page in local state so the UI reflects the new
      // status without a full reload.
      setPayload(p => ({
        ...p,
        geo_pages: p.geo_pages.map(pg =>
          pg.id === page.id ? { ...pg, approval_status: r.data.approval_status, approval_at: r.data.approval_at } : pg),
      }));
    } catch (e) {
      alert("Schválenie zlyhalo: " + (e?.response?.data?.detail || e.message));
    } finally {
      setApprovalLoading(prev => ({ ...prev, [page.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="share-loading">
        <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
      </div>
    );
  }
  if (error || !payload) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6" data-testid="share-error">
        <div className="max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-rose-400 mx-auto mb-3" />
          <h1 className="text-2xl font-black mb-2">Share link nedostupný</h1>
          <p className="text-gray-400 text-sm">{error || "Skontroluj URL alebo kontaktuj agentúru."}</p>
        </div>
      </div>
    );
  }

  const { client, branding, latest_scan, geo_pages, share } = payload;
  const summary = latest_scan?.summary || {};
  const brand = branding?.brand_color || "#7c3aed";

  return (
    <div className="min-h-screen bg-black text-white" data-testid="share-page">
      {/* Agency-branded header */}
      <header
        className="border-b border-gray-800 px-6 py-4"
        style={{ background: `linear-gradient(to bottom, ${brand}11, transparent)` }}
        data-testid="share-header"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            {branding?.logo_data_url ? (
              <img
                src={branding.logo_data_url}
                alt={branding.company_name || "Agency logo"}
                className="h-10 w-auto object-contain"
                data-testid="share-header-logo"
              />
            ) : (
              <div
                className="w-10 h-10 flex items-center justify-center font-black text-xl text-white rounded-sm"
                style={{ backgroundColor: brand }}
              >
                {(branding?.company_name || "A")[0]}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-gray-400">SEO/GEO Audit</div>
              <div className="text-lg font-bold truncate" data-testid="share-header-company">
                {branding?.company_name || "Your Agency"}
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-400 flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            Read-only zdieľaný report
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Client card */}
        <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm" data-testid="share-client-card">
          <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Klient</div>
          <h1 className="text-3xl font-black tracking-tight mb-1">{client.name}</h1>
          <div className="text-sm text-gray-400 flex items-center gap-2 flex-wrap">
            <a
              href={client.domain.startsWith("http") ? client.domain : `https://${client.domain}`}
              target="_blank" rel="noreferrer"
              className="hover:underline inline-flex items-center gap-1"
              style={{ color: brand }}
            >
              {client.domain.replace(/^https?:\/\//, "")} <ExternalLink className="w-3 h-3" />
            </a>
            <span className="text-gray-700">·</span>
            <span className="uppercase">{client.language}</span>
            {client.industry && (<><span className="text-gray-700">·</span>{client.industry}</>)}
          </div>
        </section>

        {/* Scan summary */}
        {latest_scan && summary.score != null ? (
          <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm" data-testid="share-scan-summary">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="md:col-span-1 p-4 bg-black/40 border border-gray-800 rounded-sm">
                <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Skóre</div>
                <div className="text-5xl font-black tabular-nums" style={{ color: brand }} data-testid="share-scan-score">
                  {summary.score}<span className="text-base text-gray-600 ml-1">/100</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">{summary.scanned || 0} URL scanovaných</div>
              </div>
              <div className="md:col-span-3 grid grid-cols-5 gap-2">
                {["A", "B", "C", "D", "F"].map(g => (
                  <div key={g} className={`p-3 border rounded-sm text-center ${GRADE_COLOURS[g]}`}>
                    <div className="text-xl font-black">{g}</div>
                    <div className="text-xs">{(summary.grade_counts || {})[g] || 0}</div>
                  </div>
                ))}
              </div>
            </div>

            {(summary.top_issues || []).length > 0 && (
              <div className="mt-3">
                <div className="text-xs uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3 text-amber-400" /> Top 5 problémov
                </div>
                <ul className="space-y-1 text-sm">
                  {summary.top_issues.slice(0, 5).map((it, idx) => (
                    <li key={idx} className="flex items-center justify-between gap-2 p-2 bg-black/30 rounded-sm">
                      <span className="text-gray-300"><b className="text-purple-400 mr-2">{idx + 1}.</b>{it.issue}</span>
                      <span className="text-xs text-gray-500 tabular-nums">×{it.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        ) : (
          <section className="p-5 bg-amber-900/20 border border-amber-700/40 rounded-sm text-sm text-amber-200" data-testid="share-no-scan">
            <AlertCircle className="w-4 h-4 inline mr-2" />
            Pre tohto klienta zatiaľ neexistuje žiadny scan. Agentúra spustí audit a výsledok sa objaví tu.
          </section>
        )}

        {/* GEO pages with approval */}
        {geo_pages.length > 0 && (
          <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm" data-testid="share-geo-pages">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-400" />
                Lokálne SEO stránky ({geo_pages.length})
              </h2>
              {share.allow_approval && (
                <div className="text-[10px] text-gray-500">
                  Schvál alebo zamietni jednotlivé stránky pred ich publikovaním.
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {geo_pages.map((p, i) => {
                const Icon = TYPE_ICONS[p.template_type] || ShoppingCart;
                const status = p.approval_status;
                return (
                  <article key={p.id} className="p-3 bg-black/40 border border-gray-800 rounded-sm" data-testid={`share-page-card-${i}`}>
                    <div className="flex items-start gap-2 mb-2">
                      <Icon className="w-3.5 h-3.5 text-gray-500 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold line-clamp-2" title={p.envelope?.title}>
                          {p.envelope?.title || "—"}
                        </div>
                        <div className="text-[10px] text-gray-500 mt-1 inline-flex items-center gap-1">
                          <MapPin className="w-2.5 h-2.5" /> {p.city?.name} ({p.city?.country})
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 line-clamp-2 mb-3" title={p.envelope?.meta_description}>
                      {p.envelope?.meta_description || "—"}
                    </p>
                    <div className="flex items-center gap-1 flex-wrap">
                      <button
                        onClick={() => openPreview(p)}
                        data-testid={`share-page-preview-${i}`}
                        className="px-2 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded-sm inline-flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" /> Náhľad
                      </button>
                      {share.allow_approval && status !== "approved" && (
                        <button
                          onClick={() => handleApproval(p, "approved")}
                          disabled={approvalLoading[p.id]}
                          data-testid={`share-page-approve-${i}`}
                          className="px-2 py-1 text-xs bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white rounded-sm inline-flex items-center gap-1"
                        >
                          <CheckCircle className="w-3 h-3" /> Schváliť
                        </button>
                      )}
                      {share.allow_approval && status !== "rejected" && (
                        <button
                          onClick={() => handleApproval(p, "rejected")}
                          disabled={approvalLoading[p.id]}
                          data-testid={`share-page-reject-${i}`}
                          className="px-2 py-1 text-xs bg-rose-900/60 hover:bg-rose-900 disabled:opacity-40 text-rose-200 rounded-sm inline-flex items-center gap-1"
                        >
                          <XCircle className="w-3 h-3" /> Zamietnuť
                        </button>
                      )}
                      {status && (
                        <span
                          data-testid={`share-page-status-${i}`}
                          className={`ml-auto px-2 py-0.5 text-[10px] uppercase tracking-widest rounded-sm ${
                            status === "approved"
                              ? "bg-emerald-900/40 text-emerald-300 border border-emerald-700/40"
                              : "bg-rose-900/40 text-rose-300 border border-rose-700/40"
                          }`}
                        >
                          {status === "approved" ? "Schválené" : "Zamietnuté"}
                        </span>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {/* Footer — VIRAL channel, intentionally NOT toggleable here */}
        <footer className="pt-6 border-t border-gray-800 text-center" data-testid="share-footer">
          <a
            href="https://toprankpulse.com"
            target="_blank" rel="noreferrer"
            className="text-xs text-gray-500 hover:text-purple-400 inline-flex items-center gap-1"
          >
            Powered by <span className="font-bold text-purple-400">TopRankPulse</span>
            <span className="text-gray-700">·</span>
            AI SEO &amp; GEO Specialist
          </a>
        </footer>
      </main>

      {/* Preview iframe modal */}
      {previewPage && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8 bg-black/80 backdrop-blur-sm"
          onClick={() => setPreviewPage(null)}
          data-testid="share-preview-modal"
        >
          <div
            className="relative w-full max-w-5xl h-[90vh] bg-gray-950 border border-purple-700/40 rounded-sm shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-purple-400">Náhľad</div>
                <div className="text-sm font-bold truncate">{previewPage.envelope?.title}</div>
              </div>
              <button onClick={() => setPreviewPage(null)} data-testid="share-preview-close" className="p-1 text-gray-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="flex-1 overflow-hidden bg-white">
              {previewHtml ? (
                <iframe title="Page preview" srcDoc={previewHtml} sandbox="" className="w-full h-full border-0" data-testid="share-preview-iframe" />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
